/*
 * Team Branding — name / short name / logo / banner.
 *
 * What changed from the v1 of this screen:
 *   - Color swatches removed. Teams default to red/blue from the event
 *     seed; we no longer let captains pick arbitrary palettes.
 *   - Motto removed.
 *   - Logo URL replaced with an upload + crop widget. The user picks an
 *     image from their device, drags and zooms to compose what fits the
 *     circular display, and the cropped result is stored as a base64
 *     JPEG data URL in the existing logo_url column. No new schema.
 *
 * Why hand-rolled crop instead of a library:
 *   - One-off use; avoiding the dependency keeps the bundle lean.
 *   - The interaction we need is small: pan, zoom, save. No rotate, no
 *     aspect-ratio choices.
 *   - Output is square (512×512). The app displays it inside a CSS
 *     border-radius:50% so it reads as a circle everywhere.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { api } from '@/lib/api';
import { useTournamentStore } from '@/store/tournamentStore';
import { useAuthStore } from '@/store/authStore';
import { AppShell } from '@/components/AppShell';
import { ImageCropUpload } from '@/components/ImageCropUpload';

export default function TeamBranding() {
  const { eventId, teamId } = useParams<{ eventId: string; teamId: string }>();
  const navigate = useNavigate();
  const { activeEvent, setActiveEvent } = useTournamentStore();

  const team = activeEvent?.teams.find((t) => t.id === teamId) ?? null;

  // Branding edit is owner or this team's captain only. Backend enforces
  // it; frontend kicks non-allowed roles back to Teams so they don't see
  // an editable-looking form that will 403 on save. Resolve the caller's
  // event_player via the auth store's userId so we can match the captain
  // pointer on the team.
  const myUserId = useAuthStore((s) => s.userId);
  useEffect(() => {
    if (!activeEvent || activeEvent.id !== eventId || !myUserId) return;
    const isOwner = activeEvent.myRole === 'owner';
    const myEp = activeEvent.players.find((p) => p.userId === myUserId);
    const targetTeam = activeEvent.teams.find((t) => t.id === teamId) ?? null;
    const isThisTeamCaptain = activeEvent.myRole === 'captain'
      && !!myEp
      && targetTeam?.captainEventPlayerId === myEp.id;
    if (!isOwner && !isThisTeamCaptain) {
      navigate(`/app/tournaments/${eventId}/teams`, { replace: true });
    }
  }, [activeEvent, eventId, teamId, navigate, myUserId]);

  const [loading, setLoading] = useState(!team);
  const [name, setName] = useState(team?.name ?? '');
  // logoUrl carries either the saved value from the DB or, after the
  // cropper produces a new one, a fresh data: URL.
  const [logoUrl, setLogoUrl] = useState(team?.logoUrl ?? '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!eventId || team) return;
    try {
      const ev = await api.getTournament(eventId);
      setActiveEvent(ev);
      const t = ev.teams.find((x) => x.id === teamId);
      if (t) {
        setName(t.name);
        setLogoUrl(t.logoUrl ?? '');
      }
    } finally {
      setLoading(false);
    }
  }, [eventId, teamId, team, setActiveEvent]);

  useEffect(() => { load(); }, [load]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!teamId || !eventId) return;
    if (name.trim().length < 1) { setError('Team name is required.'); return; }
    setError(null); setSaving(true);
    try {
      await api.updateTournamentTeam(teamId, {
        name: name.trim(),
        // Clear short_name on every save so teams are identified by
        // their full name only. Colors, motto, banner stay COALESCEd.
        shortName: null,
        logoUrl: logoUrl || null,
      });
      const ev = await api.getTournament(eventId);
      setActiveEvent(ev);
      navigate(`/app/tournaments/${eventId}/teams`, { replace: true });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save');
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId}/teams`, label: 'Teams' }}>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId}/teams`, label: 'Teams' }}>
      <section className="t-hero">
        <h1>Team Branding</h1>
        <p>Set the team name and logo. Both appear throughout the event.</p>
      </section>

      {error && <div className="t-error">{error}</div>}

      <form className="t-form" onSubmit={handleSubmit}>
        <div className="t-field">
          <label className="t-label" htmlFor="tb-name">Team name</label>
          <input id="tb-name" className="t-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoFocus />
        </div>

        <div className="t-field">
          <div className="t-label">Team logo</div>
          <ImageCropUpload
            value={logoUrl}
            onChange={setLogoUrl}
            emptyLabel="Upload logo"
            alt="Team logo"
          />
        </div>

        <button type="submit" className="t-btn t-btn-primary t-btn-block" disabled={saving}>
          {saving ? 'Saving…' : 'Save Branding'}
        </button>
      </form>
    </AppShell>
  );
}
